import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Chip,
  IconButton,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import { formatDateInNewfoundland } from "../../../utils/dates/formatDateInNewfoundland";
import dayjs from "dayjs";

const statusColors = {
  new: "primary",
  read: "info",
  responded: "success",
  archived: "default",
};

const isVisuallyNew = (msg) =>
  msg.status === "new" &&
  dayjs(msg.submissionDate || msg.createdAt).isAfter(
    dayjs().subtract(3, "day")
  );

const ContactMessagesTable = ({
  messages,
  onStatusUpdate,
  onRequestDelete,
  statusOptions,
  sortConfig,
  setSortConfig,
}) => {
  const [openRowId, setOpenRowId] = useState(null);

  const toggleRow = (id, status) => {
    const isOpen = openRowId === id;
    if (!isOpen && status === "new") {
      onStatusUpdate(id, "read");
    }
    setOpenRowId(isOpen ? null : id);
  };

  const renderSortableHeader = (label, field) => {
    const isActive = sortConfig.field === field;
    const isAsc = sortConfig.direction === "asc";

    return (
      <Box
        onClick={() => {
          if (isActive) {
            setSortConfig({ field, direction: isAsc ? "desc" : "asc" });
          } else {
            setSortConfig({ field, direction: "asc" });
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center", // center the whole label+icon horizontally
          gap: 0.5,
          cursor: "pointer",
          userSelect: "none",
          whiteSpace: "nowrap",
        }}>
        <Typography fontWeight="bold">{label}</Typography>

        {isActive ? (
          isAsc ? (
            <KeyboardArrowUpIcon fontSize="small" />
          ) : (
            <KeyboardArrowDownIcon fontSize="small" />
          )
        ) : (
          <Box
            sx={{
              width: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ccc",
              "&:hover": { color: "#999" },
            }}>
            <KeyboardArrowUpIcon fontSize="small" />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{renderSortableHeader("Name", "firstName")}</TableCell>
            <TableCell>{renderSortableHeader("Email", "email")}</TableCell>
            <TableCell>
              {renderSortableHeader("Phone", "phoneNumber")}
            </TableCell>
            <TableCell>
              {renderSortableHeader("Submitted", "submissionDate")}
            </TableCell>
            <TableCell>{renderSortableHeader("Status", "status")}</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {messages.map((msg) => (
            <React.Fragment key={msg._id}>
              <TableRow
                hover
                selected={openRowId === msg._id}
                onClick={() => toggleRow(msg._id, msg.status)}
                sx={{
                  cursor: "pointer",
                  backgroundColor: isVisuallyNew(msg) ? "#fff8e1" : "inherit",
                }}>
                <TableCell>{`${msg.firstName} ${msg.lastName}`}</TableCell>
                <TableCell>{msg.email}</TableCell>
                <TableCell>{msg.phoneNumber}</TableCell>
                <TableCell>
                  {formatDateInNewfoundland(
                    msg.submissionDate || msg.createdAt,
                    "MMM D, YYYY [at] h:mm A"
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={isVisuallyNew(msg) ? "new" : msg.status}
                    color={
                      isVisuallyNew(msg)
                        ? "primary"
                        : statusColors[msg.status] || "default"
                    }
                    size="small"
                    sx={{ textTransform: "capitalize", fontSize: "0.75rem" }}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onRequestDelete(msg._id);
                    }}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell colSpan={6} sx={{ py: 0 }}>
                  <Collapse
                    in={openRowId === msg._id}
                    timeout="auto"
                    unmountOnExit>
                    <Box sx={{ m: 2 }}>
                      <Typography variant="subtitle2">Message</Typography>
                      <Typography
                        variant="body2"
                        whiteSpace="pre-wrap"
                        sx={{ mt: 0.5 }}>
                        {msg.message}
                      </Typography>

                      <Box sx={{ mt: 2 }}>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={msg.status}
                            onChange={(e) =>
                              onStatusUpdate(msg._id, e.target.value)
                            }>
                            {statusOptions.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ContactMessagesTable;
